import { defineStore } from "pinia";
import api from "@/api";
import {
  ActivityData,
  ActivitySummaryPerClassData,
} from "@/interfaces/activities.interface";
import { StudentData } from "@/interfaces/student-data.interface";

export const useActivityStore = defineStore({
  id: "activityStore",
  state: () => ({
    activityData: null as ActivityData | null,
    dailyTasksData: null,
    loginActivityData: null,
    activitySummaryPerClassData: null as ActivitySummaryPerClassData[] | null,
    activitySummaryPerStudent: null as StudentData[] | null,
    loader: {
      fetchActivityData: false,
      fetchDailyTasksData: false,
      fetchLoginActivityData: false,
      fetchActivitySummaryPerClassData: false,
      fetchActivitySummaryPerStudent: false,
    },
  }),
  getters: {
    getActivityData(): ActivityData | null {
      return this.activityData;
    },
    getDailyTasksData(): any {
      return this.dailyTasksData;
    },
    getLoginActivityData(): any {
      return this.loginActivityData;
    },
    getActivitySummaryPerClassData(): ActivitySummaryPerClassData[] | null {
      return this.activitySummaryPerClassData;
    },
    getActivitySummaryPerStudent(): StudentData[] | null {
      return this.activitySummaryPerStudent;
    },
  },
  actions: {
    /**
     * Fetch user engagement (app activity data) based on specified filters
     */
    async fetchActivityData(payload: any) {
      this.loader.fetchActivityData = true;
      this.activityData = null;

      try {
        const response = await api.post(
          "/v2/data/activity/fetch-activity-data",
          payload
        );

        if (response.status === 200) {
          this.activityData = response.data.data;
        } else {
          throw new Error("Failed to fetch activity data.");
        }
      } catch (error) {
        console.error("Error fetching activity data: ", error);
        this.activityData = null;
      } finally {
        this.loader.fetchActivityData = false;
      }
    },

    /**
     * Fetch daily tasks completion data for users based on specified filters
     */
    async fetchDailyTasksData(payload: any) {
      this.loader.fetchDailyTasksData = true;
      this.dailyTasksData = null;

      try {
        const response = await api.post(
          "/v2/data/activity/fetch-daily-tasks-data",
          payload
        );

        if (response.status === 200) {
          this.dailyTasksData = response.data.data;
        } else {
          throw new Error("Failed to fetch daily tasks data.");
        }
      } catch (error) {
        console.error("Error fetching daily tasks data: ", error);
        this.dailyTasksData = null;
      } finally {
        this.loader.fetchDailyTasksData = false;
      }
    },

    /**
     * Fetch unique daily login activity entries of the filtered learners
     */
    async fetchLoginActivityData(payload: any) {
      this.loader.fetchLoginActivityData = true;
      this.loginActivityData = null;

      try {
        const response = await api.post(
          "/v2/data/activity/fetch-login-activity-data",
          payload
        );

        if (response.status === 200) {
          this.loginActivityData = response.data.data;
        } else {
          throw new Error("Failed to fetch login activity data.");
        }
      } catch (error) {
        console.error("Error fetching login activity data: ", error);
        this.loginActivityData = null;
      } finally {
        this.loader.fetchLoginActivityData = false;
      }
    },

    /**
     * Fetch activity summary data per class group when the class filter value is null
     */
    async fetchActivitySummaryPerClassData(payload: any) {
      this.loader.fetchActivitySummaryPerClassData = true;

      try {
        const response = await api.post(
          "/v2/data/activity/fetch-activity-summary-per-class-group",
          payload
        );

        if (response.status === 200) {
          this.activitySummaryPerClassData = response.data.data;
        } else {
          throw new Error("Failed to fetch activity summary per class data.");
        }
      } catch (error) {
        console.error(
          "Error fetching activity summary per class data: ",
          error
        );
        this.activitySummaryPerClassData = null;
      } finally {
        this.loader.fetchActivitySummaryPerClassData = false;
      }
    },

    /**
     * Fetch activity summary data per student when the class filter value is populated
     */
    async fetchActivitySummaryPerStudent(payload: any) {
      this.loader.fetchActivitySummaryPerStudent = true;
      this.activitySummaryPerStudent = null;

      try {
        const response = await api.post(
          "/v2/data/activity/fetch-activity-summary-per-student",
          payload
        );

        if (response.status === 200) {
          this.activitySummaryPerStudent = response.data.data;
        } else {
          throw new Error("Failed to fetch activity summary per student data.");
        }
      } catch (error) {
        console.error(
          "Error fetching activity summary per student data: ",
          error
        );
        this.activitySummaryPerStudent = null;
      } finally {
        this.loader.fetchActivitySummaryPerStudent = false;
      }
    },
  },
  persist: true,
});
